import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPsap, PSAPInfo } from '@rsos/reduxDataStore';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { getTokens } from '@rsos/sinatra';
import { checkStatus } from '@rsos/utils';
import { getAPIHost } from '@rsos/utils/metaTags';
import { AdminState } from '../../../reducers';

export type MapLayerSettingsState = {
  initialized: boolean;
  updating: boolean;
  settings: PSAPInfo['map_layer_settings'];
};

const initialState: MapLayerSettingsState = {
  initialized: false,
  updating: false,
  settings: {},
};

const baseURL = `${getAPIHost()}/v1/capstone`;
export const saveMapLayerSettings = createAsyncThunk<
  PSAPInfo['map_layer_settings'],
  PSAPInfo['map_layer_settings'],
  { state: AdminState }
>(
  'mapLayerSettings/save',

  async (mapLayerSettings, { getState }) => {
    const state = getState();
    const tokens = getTokens();
    const org = organizationSelector(state);
    if (!org || !tokens) {
      throw 'Cannot save settings';
    }
    const psapPatch = {
      map_layer_settings: mapLayerSettings,
    };

    const endpoint = `${baseURL}/psaps/${org.name}`;
    const options = {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${tokens.token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(psapPatch),
    };

    return fetch(endpoint, options)
      .then(checkStatus)
      .then(r => r.json())
      .then(psap => (psap as unknown as PSAPInfo).map_layer_settings);
  },
);

const mapLayerSettingsSlice = createSlice({
  name: 'mapLayerSettings',
  initialState,
  reducers: {
    setMapLayerSettings(
      state,
      { payload }: { payload: MapLayerSettingsState['settings'] },
    ) {
      state.settings = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(saveMapLayerSettings.pending, (state, action) => {
        state.updating = true;
        // Optimistic update:
        state.settings = action.meta.arg;
      })
      .addCase(saveMapLayerSettings.fulfilled, (state, action) => {
        state.updating = false;
        state.settings = action.payload;
      })
      .addCase(saveMapLayerSettings.rejected, state => {
        state.updating = false;
      })
      .addCase(fetchPsap.fulfilled, (state, action) => {
        state.initialized = true;
        state.settings = action.payload.map_layer_settings;
      });
  },
});

export const { setMapLayerSettings } = mapLayerSettingsSlice.actions;

export const mapLayerSettingsSelector = (state: {
  mapLayerSettings: MapLayerSettingsState;
}) => {
  return state.mapLayerSettings?.settings || {};
};
export const mapLayerSettingsSavingSelector = (state: {
  mapLayerSettings: MapLayerSettingsState;
}) => {
  return !!state.mapLayerSettings?.updating;
};
export const mapLayerSettingsReducer = mapLayerSettingsSlice.reducer;
export default mapLayerSettingsReducer;
